// assets/header-cascade-menu.js
// Defines the <cascade-menu> custom element for the desktop cascade navigation.

const SEL_EXPAND       = '[data-expand]';
const SEL_ACTIVE_ITEM  = '.cascade-menu__item--active';
const SEL_HAS_CHILDREN = '.cascade-menu__item--has-children';
const SEL_FOCUSABLES   = '.cascade-menu__link, .cascade-menu__item-btn';

class CascadeMenu extends HTMLElement {
  connectedCallback() {
    this._trigger = this.querySelector('[data-cascade-trigger]');
    this._panel   = this.querySelector('.cascade-menu__panel');
    this._cols    = [
      null,
      this.querySelector('[data-column="1"]'),
      this.querySelector('[data-column="2"]'),
      this.querySelector('[data-column="3"]'),
    ];
    this._col2Heading = this.querySelector('[data-col-heading]');
    this._col3Heading = this.querySelector('[data-col3-heading]');

    if (!this._trigger || !this._panel) return;

    // Store bound references so disconnectedCallback can remove them.
    this._boundTriggerClick      = this._onTriggerClick.bind(this);
    this._boundPanelClick        = this._onPanelClick.bind(this);
    this._boundPanelKeyDown      = this._onPanelKeyDown.bind(this);
    this._boundDocClick          = this._onDocumentClick.bind(this);
    this._boundDocKeyDown        = this._onDocumentKeyDown.bind(this);
    this._boundHeaderMenuToggle  = this._onHeaderMenuToggle.bind(this);

    this._trigger.addEventListener('click', this._boundTriggerClick);
    this._panel.addEventListener('click', this._boundPanelClick);
    this._panel.addEventListener('keydown', this._boundPanelKeyDown);
    document.addEventListener('click', this._boundDocClick);
    document.addEventListener('keydown', this._boundDocKeyDown);

    // Close the cascade panel when any <header-menu> dropdown opens.
    // toggle does not bubble, so we attach to each <details> individually.
    this.querySelectorAll('header-menu details').forEach((d) => {
      d.addEventListener('toggle', this._boundHeaderMenuToggle);
    });

    // Attach hover listeners to col1 (static content rendered in the template).
    this._attachHoverListeners(this._cols[1]);

    // Close when StickyHeader hides the header (shopify-section-header-hidden
    // class is added to .section-header). We cannot modify global.js, so we
    // watch via MutationObserver instead of relying on closeMenuDisclosure().
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) {
      this._stickyObserver = new MutationObserver(() => {
        if (sectionHeader.classList.contains('shopify-section-header-hidden')) {
          this.close();
        }
      });
      this._stickyObserver.observe(sectionHeader, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  disconnectedCallback() {
    this._trigger?.removeEventListener('click', this._boundTriggerClick);
    this._panel?.removeEventListener('click', this._boundPanelClick);
    this._panel?.removeEventListener('keydown', this._boundPanelKeyDown);
    document.removeEventListener('click', this._boundDocClick);
    document.removeEventListener('keydown', this._boundDocKeyDown);
    this.querySelectorAll('header-menu details').forEach((d) => {
      d.removeEventListener('toggle', this._boundHeaderMenuToggle);
    });
    if (this._stickyObserver) this._stickyObserver.disconnect();
  }

  // Public API — matches the signature expected by future callers.
  // Pass closeDropdowns=false when a <header-menu> just opened and triggered
  // this close — we must not immediately shut the dropdown the user opened.
  close(closeDropdowns = true) {
    if (!this._panel || this._panel.dataset.open !== 'true') return;
    this._panel.dataset.open = 'false';
    this._panel.setAttribute('aria-hidden', 'true');
    this._trigger.setAttribute('aria-expanded', 'false');
    if (this._col2Heading) this._col2Heading.textContent = '';
    if (this._col3Heading) this._col3Heading.textContent = '';
    // Close any open <header-menu> dropdowns via their own close() so that
    // aria-expanded is also reset (DetailsDisclosure.close handles both).
    if (closeDropdowns) this.querySelectorAll('header-menu').forEach((m) => m.close());
  }

  open() {
    this._panel.dataset.open = 'true';
    this._panel.setAttribute('aria-hidden', 'false');
    this._trigger.setAttribute('aria-expanded', 'true');
    // Reset columns 2 & 3 and clear active state so the panel is fresh.
    this._cols[2].replaceChildren();
    this._cols[3].replaceChildren();
    if (this._col2Heading) this._col2Heading.textContent = '';
    if (this._col3Heading) this._col3Heading.textContent = '';
    this._cols[1].querySelectorAll(SEL_ACTIVE_ITEM).forEach((el) => {
      el.classList.remove('cascade-menu__item--active');
      const btn = el.querySelector(SEL_EXPAND);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
    // Auto-expand the first L1 item that has children so column 2 is
    // never empty on initial open.
    const firstExpandable = this._cols[1].querySelector(SEL_HAS_CHILDREN);
    if (firstExpandable) this._expandItem(firstExpandable, 1);
  }

  // ── Private handlers ──────────────────────────────────────────────────────

  _onTriggerClick(event) {
    // stopPropagation prevents the document click handler from seeing this
    // same event and immediately closing the panel we just toggled.
    event.stopPropagation();
    this._panel.dataset.open === 'true' ? this.close() : this.open();
  }

  _onPanelClick(event) {
    // Keep clicks inside the panel from reaching _onDocumentClick.
    event.stopPropagation();

    // Immediately hide the panel before navigation so the View Transitions
    // snapshot doesn't capture the open state. Re-adding `hidden` is
    // synchronous (display:none) — no transition delay unlike close().
    if (event.target.closest('a[href]')) this._panel.hidden = true;
  }

  _attachHoverListeners(container) {
    container.querySelectorAll(SEL_EXPAND).forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        const item = btn.closest('.cascade-menu__item');
        if (!item || item.classList.contains('cascade-menu__item--active')) return;
        const colList  = item.closest('[data-column]');
        const colIndex = parseInt(colList.dataset.column, 10);
        this._expandItem(item, colIndex);
      });
    });
  }

  _expandItem(item, colIndex) {
    const template = item.querySelector(':scope > template.cascade-menu__children');
    if (!template) return;

    const sourceCol = this._cols[colIndex];
    sourceCol.querySelectorAll(SEL_ACTIVE_ITEM).forEach((el) => {
      el.classList.remove('cascade-menu__item--active');
      const btn = el.querySelector(SEL_EXPAND);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
    item.classList.add('cascade-menu__item--active');
    const expandBtn = item.querySelector(SEL_EXPAND);
    if (expandBtn) expandBtn.setAttribute('aria-expanded', 'true');

    // Populate the next column with a clone of the template content, then
    // attach hover listeners to the newly rendered expand buttons.
    const targetCol = this._cols[colIndex + 1];
    targetCol.replaceChildren(template.content.cloneNode(true));
    this._attachHoverListeners(targetCol);

    if (colIndex === 1) {
      // Clear col3 and its heading, then set col2 heading to the active L1 title.
      this._cols[3].replaceChildren();
      if (this._col2Heading) this._col2Heading.textContent = item.dataset.title || '';
      if (this._col3Heading) this._col3Heading.textContent = '';
      // Auto-expand the first L2 item that has children so col3 is never empty.
      const firstL2 = this._cols[2].querySelector(SEL_HAS_CHILDREN);
      if (firstL2) this._expandItem(firstL2, 2);
    } else if (colIndex === 2) {
      // Set col3 heading to the active L2 item's title.
      if (this._col3Heading) this._col3Heading.textContent = item.dataset.title || '';
    }
  }

  _onHeaderMenuToggle(event) {
    if (event.target.open) this.close(false);
  }

  _onDocumentClick(event) {
    // Close when the user clicks anywhere outside this custom element.
    if (!this.contains(event.target)) this.close();
  }

  _onDocumentKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (this._panel.dataset.open !== 'true') return;
    this.close();
    this._trigger.focus();
  }

  _onPanelKeyDown(event) {
    const colList = event.target.closest('[data-column]');
    if (!colList) return;

    const colIndex   = parseInt(colList.dataset.column, 10);
    const focusables = Array.from(colList.querySelectorAll(SEL_FOCUSABLES));
    const currentIdx = focusables.indexOf(event.target);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (currentIdx < focusables.length - 1) focusables[currentIdx + 1].focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (currentIdx > 0) focusables[currentIdx - 1].focus();
        break;

      case 'ArrowRight': {
        event.preventDefault();
        const nextCol = this._cols[colIndex + 1];
        if (!nextCol) break;
        const firstFocusable = nextCol.querySelector(SEL_FOCUSABLES);
        if (firstFocusable) firstFocusable.focus();
        break;
      }

      case 'ArrowLeft': {
        event.preventDefault();
        const prevCol = this._cols[colIndex - 1];
        if (!prevCol) break;
        // Return focus to the active item in the previous column if one exists.
        const activeLink =
          prevCol.querySelector(`${SEL_ACTIVE_ITEM} .cascade-menu__item-btn`) ||
          prevCol.querySelector(`${SEL_ACTIVE_ITEM} .cascade-menu__link`) ||
          prevCol.querySelector('.cascade-menu__item-btn') ||
          prevCol.querySelector('.cascade-menu__link');
        if (activeLink) activeLink.focus();
        break;
      }
    }
  }
}

if (!customElements.get('cascade-menu')) {
  customElements.define('cascade-menu', CascadeMenu);
}
